/**
 * SWYM CUSTOM ADD TO WISHLIST COMPONENT
 *
 * A custom web component that provides wishlist functionality for products.
 * Integrates with the SWYM wishlist platform through utilities-swym.js.
 *
 * Features:
 * - Automatic product detection from parent components (product-overview, product-bundle-item, etc.)
 * - Dynamic state management (add/remove from wishlist)
 * - Customer authentication handling with login redirect
 * - Success notifications with product imagery
 * - Responsive to variant changes and wishlist updates
 * - Debug mode support for development
 *
 * Usage:
 * <swym-custom-add-to-wishlist data-customer="true" class="debug">
 *   <!-- Templates for different states -->
 * </swym-custom-add-to-wishlist>
 *
 * @class swymCustomAddToWishlist
 * @extends HTMLElement
 */

import {
  swymGetDefaultWishlist,
  swymProductsAreInList,
  swymAddProductsToList,
  swymRemoveProductsFromList,
  swymBuildSuccessNotification,
  swymShowSuccessNotification,
  swymWaitForInitialization,
  swymIsInitialized
} from '@utilities/swym';
import { EVENTS, subscribe } from '@utilities/events';

if(!customElements.get('swym-custom-add-to-wishlist')) {
  class swymCustomAddToWishlist extends HTMLElement {

    /**
     * Constructor - Initialize component state
     */
    constructor() {
      super();
      this.initCalled = false;
    }

    /**
     * Check if debug mode is enabled
     * @returns {boolean} True if the component has the 'debug' class
     */
    allowDebug() {
      return this.classList.contains('debug');
    }

    /**
     * Initialize the wishlist component
     * Handles SWYM initialization, product data gathering, and component rendering
     * @async
     * @returns {Promise<void>}
     */
    async init(){
      // if the component has already been initialized, do not re-init.
      if(this.initCalled) return;
      this.initCalled = true;
      this.hasCustomer = this.dataset.customer ? true : false;

      // gather elements
      this.setElements();

      // gather data for the product.
      this.setComponentProducts();
      if(this.allowDebug()) console.log("swym-custom-add-to-wishlist.js: componentProducts", this.componentProducts, this);

      // Wait for SWYM utilities to be initialized before proceeding
      if(!this.elements.loginLink && swymIsInitialized()){
        await this.initializeWithSwym();
      } else if(!this.elements.loginLink) {
        // Wait for SWYM to initialize, then proceed
        try {
          await swymWaitForInitialization();
          await this.initializeWithSwym();
        } catch (error) {
          console.error("swym-custom-add-to-wishlist.js: Failed to wait for SWYM initialization", error);
        }
      } else {
        if(this.allowDebug()) console.log("swym-custom-add-to-wishlist.js: no customer present, exiting component render.", this);
      }

      // set the event listeners.
      this.setEventListeners();
    }

    /**
     * Initialize component with SWYM utilities
     * Called after SWYM is confirmed to be loaded and initialized
     * @async
     * @returns {Promise<void>}
     */
    async initializeWithSwym() {
      if(this.allowDebug()) console.log("swym-custom-add-to-wishlist.js: customer present, performing component render.", this);

      // Get the default wishlist from utilities
      const defaultWishlist = swymGetDefaultWishlist();
      this.areProductsInWishlist = swymProductsAreInList(this.componentProducts, defaultWishlist);

      if(this.allowDebug()) console.log("swym-custom-add-to-wishlist.js: are component products in the current wishlist?", this.areProductsInWishlist, this);

      // now that we have our default wishlist, we will render the component.
      this.renderComponent();
    }

    /**
     * Set up DOM element references
     * Caches commonly used elements for performance
     * @private
     */
    setElements(){
      this.elements = {};
      this.elements.templates = {};
      this.elements.templates.noCustomer = this.querySelector(`template#swym-no-customer`);
      this.elements.templates.addToWishlist = this.querySelector(`template#swym-add-to-wishlist-button`);
      this.elements.templates.addedToWishlist = this.querySelector(`template#swym-added-to-wishlist-button`);
      this.elements.activeState = this.querySelector('.swym-wishlist-state');
      this.elements.closestForm = this.closest('form');
      this.elements.closestBundleItem = this.closest('product-bundle-item');
      this.elements.closestProductOverview = this.closest('product-overview');
      this.elements.closesProductBundleOverview = this.closest('product-bundle-overview');
      this.elements.loginLink = this.querySelector('a.swym-login-link');
    }

    /**
     * Set up event listeners for component interactions and variant changes
     * Listens for click events and variant change events from parent components
     * @private
     */
    setEventListeners() {
      this.addEventListener('click', this.handleClick.bind(this));
      this.unsubscribers = [];

      if (this.elements.closestProductOverview) {
        this.unsubscribers.push(
          subscribe(EVENTS.PRODUCT_OVERVIEW_VARIANT_CHANGE, async (data) => {
            if (data.productOverview !== this.elements.closestProductOverview) return;
            await this.refreshComponentState();
          })
        );
      }

      if (this.elements.closestBundleItem) {
        document.addEventListener('product-bundle-item:variant-change', async (event) => {
          if (event.target.isEqualNode(this.elements.closestBundleItem)) {
            await this.refreshComponentState();
          }
        });
      }

      if (this.elements.closesProductBundleOverview && !this.elements.closestBundleItem) {
        document.addEventListener('product-bundle-overview:variant-change', async (event) => {
          if (event.target.isEqualNode(this.elements.closesProductBundleOverview)) {
            await this.refreshComponentState();
          }
        });
      }
    }

    /**
     * Get enriched product data for notifications
     * Converts SWYM product data to format expected by utilities notification system
     * @returns {Array} Array of enriched product objects for notifications
     * @private
     */
    getEnrichedProductsForNotification(){
      const enrichedProducts = [];
      const parentComponent = this.getActiveParentComponent();

      if (parentComponent.type === 'productOverview') {
        const product = parentComponent.getProduct();
        const variant = parentComponent.getVariant();
        if(Object.keys(variant).length > 0){
          enrichedProducts.push({
            id: variant.id,
            title: product.title,
            variant_title: variant.title,
            featured_media: variant.featured_media
          });
        }
      } else if (parentComponent.type === 'bundleItem') {
        const product = parentComponent.getProduct();
        const variant = parentComponent.getVariant();
        if(Object.keys(variant).length > 0){
          enrichedProducts.push({
            id: variant.id,
            title: product.title,
            variant_title: variant.title,
            featured_media_url: variant.featured_media_url
          });
        }
      } else if (parentComponent.type === 'bundleOverview') {
        const bundleItems = parentComponent.element.querySelectorAll('product-bundle-item');
        bundleItems.forEach((bundleItem) => {
          const product = bundleItem.getProduct?.() ?? {};
          const variant = bundleItem.getVariant?.() ?? {};
          if(Object.keys(variant).length > 0){
            enrichedProducts.push({
              id: variant.id,
              title: product.title,
              variant_title: variant.title,
              featured_media_url: variant.featured_media_url
            });
          }
        });
      }

      return enrichedProducts;
    }

    /**
     * Render success notification using SWYM utilities
     * @private
     */
    renderSuccessMessage(){
      const products = this.getEnrichedProductsForNotification();
      const successMessage = swymBuildSuccessNotification(products);
      swymShowSuccessNotification(successMessage);
    }

    /**
     * Refresh the component state after external changes
     * Re-evaluates product data and wishlist status, then re-renders
     * @async
     * @returns {Promise<void>}
     */
    async refreshComponentState() {
      if(this.allowDebug()) console.log("swym-custom-add-to-wishlist.js: refreshComponentState()", this);
      this.setComponentProducts();
      if(this.allowDebug()) console.log("swym-custom-add-to-wishlist.js: componentProducts", this.componentProducts, this);

      // Use the new utilities to get default wishlist and check products
      const defaultWishlist = swymGetDefaultWishlist();
      this.areProductsInWishlist = swymProductsAreInList(this.componentProducts, defaultWishlist);

      if(this.allowDebug()) console.log("swym-custom-add-to-wishlist.js: are component products in the current wishlist?", this.areProductsInWishlist, this);
      this.renderComponent();
    }

    /**
     * Get the active parent component type and data
     * @returns {Object} Object with type and data properties
     * @private
     */
    getActiveParentComponent() {
      if(this.elements.closestProductOverview) {
        return {
          type: 'productOverview',
          element: this.elements.closestProductOverview,
          getProduct: () => this.elements.closestProductOverview.getProduct(),
          getVariant: () => this.elements.closestProductOverview.getVariant(),
          getVariants: () => [this.elements.closestProductOverview.getVariant()]
        };
      } else if(this.elements.closestBundleItem) {
        return {
          type: 'bundleItem',
          element: this.elements.closestBundleItem,
          getProduct: () => this.elements.closestBundleItem.getProduct(),
          getVariant: () => this.elements.closestBundleItem.getVariant(),
          getVariants: () => [this.elements.closestBundleItem.getVariant()]
        };
      } else if(this.elements.closesProductBundleOverview) {
        return {
          type: 'bundleOverview',
          element: this.elements.closesProductBundleOverview,
          getVariants: () => this.elements.closesProductBundleOverview.getBundleVariants()
        };
      }
      return { type: null, element: null };
    }

    /**
     * Set component products by extracting data from parent components
     * @private
     */
    setComponentProducts() {
      if(this.allowDebug()) console.log("swym-custom-add-to-wishlist.js: setComponentProducts.", this);
      this.componentProducts = this.extractProductData();
    }

    /**
     * Extract product data from parent components
     * Supports product-overview, product-bundle-item, and product-bundle-overview
     * @returns {Array} Array of product objects with epi, empi, and du properties
     * @private
     */
    extractProductData() {
      const products = [];
      if(this.elements.closestProductOverview) {
        products.push(this.extractDataFromProductOverview());
      } else if(this.elements.closestBundleItem) {
        products.push(this.extractDataFromBundleItem());
      } else if(this.elements.closesProductBundleOverview) {
        products.push(...this.extractDataFromBundleOverview());
      }
      return products;
    }

    /**
     * Convert relative URL to absolute URL for SWYM API compatibility
     * @param {string} relativeUrl - Relative product URL
     * @returns {string} Absolute URL
     * @private
     */
    makeAbsoluteUrl(relativeUrl) {
      if (!relativeUrl) return '';
      if (relativeUrl.startsWith('http')) return relativeUrl; // Already absolute

      const absoluteUrl = `${window.location.origin}${relativeUrl}`;

      if (this.allowDebug()) {
        console.log(`swym-custom-add-to-wishlist.js: converted URL "${relativeUrl}" to "${absoluteUrl}"`);
      }

      return absoluteUrl;
    }

    /**
     * Extract product data from product-overview component
     * @returns {Object} Product object with epi, empi, and du properties
     * @private
     */
    extractDataFromProductOverview() {
      const productOverview = this.elements.closestProductOverview;
      const product = productOverview.getProduct();
      const variant = productOverview.getVariant();
      return {
        epi: variant.id,
        empi: product.id,
        du: this.makeAbsoluteUrl(variant.url)
      };
    }

    /**
     * Extract product data from product-bundle-item component
     * @returns {Object} Product object with epi, empi, and du properties
     * @private
     */
    extractDataFromBundleItem() {
      const product = this.elements.closestBundleItem.getProduct();
      const variant = this.elements.closestBundleItem.getVariant();
      return {
        epi: variant?.id,
        empi: product?.id,
        du: this.makeAbsoluteUrl(variant?.url)
      };
    }

    /**
     * Extract product data from product-bundle-overview component
     * @returns {Array} Array of product objects with epi, empi, and du properties
     * @private
     */
    extractDataFromBundleOverview() {
      const bundleItems = this.elements.closesProductBundleOverview.querySelectorAll('product-bundle-item');
      return Array.from(bundleItems).map((bundleItem) => {
        const product = bundleItem.getProduct?.() ?? {};
        const variant = bundleItem.getVariant?.() ?? {};
        return {
          epi: variant?.id,
          empi: product?.id,
          du: this.makeAbsoluteUrl(variant?.url)
        };
      });
    }

    /**
     * Get the current component products
     * @returns {Array} Array of product objects for this component
     * @public
     */
    getComponentProducts(){
      /*
        Simply return the wishlist products variable.
        This will either be an empty array (error), a single entry array (standard), or multiple entry array (bundles)
      */
      return this.componentProducts;
    }

    // productsAreInList method moved to utilities-swym.js

    /**
     * Render the appropriate component template based on current state
     * Determines whether to show add/remove wishlist button or login prompt
     * Handles disabled states for invalid product data
     * @private
     */
    renderComponent() {
      /*
        given the nearest wishlist button container (bundle item, bundle overview, product overview)
        determine if the relevant product id and variant id are in the wishlist.
        If the combo is in the wishlist, render the "added to wishlist" view, otherwise "add to wishlist"
        Check if any product data is undefined or null. If so, default to the 'addToWishlist' template
        with a 'disabled' class to indicate that adding to wishlist is not currently available.
      */

      if(!this.elements.activeState) {
        console.error('Error: swym-custom-add-to-wishlist: component state container is missing.');
        return;
      }

      let templateToUse;
      let disableComponent = false;

      if(!this.hasCustomer) {
        templateToUse = this.elements.templates.noCustomer;
        disableComponent = true;
      } else {
        const hasInvalidData = this.componentProducts.some(product => !product || !product.epi || !product.empi || !product.du);

        if (hasInvalidData) {
          templateToUse = this.elements.templates.addToWishlist;
          disableComponent = true;
        } else {
          templateToUse = this.areProductsInWishlist ? this.elements.templates.addedToWishlist : this.elements.templates.addToWishlist;
          disableComponent = false;
        }
      }

      if(!templateToUse) {
        console.error('Error: swym-custom-add-to-wishlist: template content is unavailable.');
        return;
      }

      this.elements.activeState.innerHTML = ''; // Clear existing content
      const content = document.importNode(templateToUse.content, true);
      this.elements.activeState.appendChild(content); // Insert new content

      if (disableComponent) {
        this.classList.add('disabled');
      } else {
        this.classList.remove('disabled');
      }
    }

    /**
     * Check if the component is currently disabled
     * @returns {boolean} True if the component has the 'disabled' class
     * @public
     */
    isDisabled() {
      return this.classList.contains("disabled");
    }

    /**
     * Handle click events on the wishlist button
     * Adds or removes products from wishlist based on current state
     * Shows success notification for additions
     * @async
     * @returns {Promise<void>}
     * @private
     */
    async handleClick() {
      /*
        If we have clicked the component and we are in the "added to wishlist" state, remove the current product id - variant id combo from the wishlist.
        Otherwise we are in the "add to wishlist" state, and we need to add this data to the wishlist.
        Whether we add or remove from the lists, we need to re-render the component for an updated render based on the original init code.
      */

      if(this.isDisabled()) {
        if(this.allowDebug()) console.log("swym-custom-add-to-wishlist.js: component is disabled.", this);
        return;
      }

      // this will probably never reach, but it feels clean.
      if(this.elements.loginLink) {
        if(this.allowDebug()) console.log("swym-custom-add-to-wishlist.js: no customer present, exiting click event.", this);
        return;
      }

      // at this stage we are clicking the component, without the redirect link present.

      // Get the default wishlist from utilities
      const defaultWishlist = swymGetDefaultWishlist();
      const listId = defaultWishlist?.list?.lid;

      if(!listId){
        if(this.allowDebug()) console.log("swym-custom-add-to-wishlist.js: no default wishlist present, exiting click event.", this);
        return;
      }

      const products = this.getComponentProducts();

      try {
        if(this.areProductsInWishlist) {
          await swymRemoveProductsFromList(listId, products);
          if(this.allowDebug()) console.log("swym-custom-add-to-wishlist.js: removed products from wishlist", products);
        } else {
          await swymAddProductsToList(listId, products);
          if(this.allowDebug()) console.log("swym-custom-add-to-wishlist.js: added products to wishlist", products);
          this.renderSuccessMessage();
        }
      } catch (error) {
        console.error("swym-custom-add-to-wishlist.js: Wishlist operation failed", error);
      }
    }

    /**
     * Clean up pub-sub subscriptions when element is removed from DOM
     */
    disconnectedCallback() {
      if (this.unsubscribers) {
        this.unsubscribers.forEach((unsub) => unsub());
        this.unsubscribers = [];
      }
    }

  }

  customElements.define('swym-custom-add-to-wishlist', swymCustomAddToWishlist);
}
